import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createGlobalStyle, ThemeProvider } from 'styled-components';

// Component
import { ConfigProvider } from 'antd';
import FullPageLoader from './components/FullPageLoader';
import ErrorBoundary from './components/ErrorBoundary';

// Routes
import DefaultRoutes from './router/default';
import DemoRoutes from './router/demo';
import SelectLanguage from './components/SelectLanguage';

// constant
import { APP_ENV, DEFAULT_ENVIRONMENT, MAHINDRA_SLUG } from './constant';
import theme, { colorPalette } from './theme';

// utils
import {
  generateLinkText,
  getAuthToken,
  getCompanySlugFromURL,
  removeAuthToken,
} from './utils';
import { useSession } from './Providers/SessionProviders';
import { useAxios } from './hooks';

const GlobalStyle = createGlobalStyle`
  a {
    color: ${(props) => props.theme.colorPrimary};
    text-decoration: none;
  }
`;

function App() {
  const navigate = useNavigate();
  const { fetch } = useAxios();
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    isLoading,
    currentUser,
    setIsLoading,
    setCurrentUser,
    setLoginLoading,
  } = useSession();

  useEffect(() => {
    const { pathname, search } = window.location;
    if (pathname.includes('/login/') || search.includes('?token=')) {
      removeAuthToken();
      setCurrentUser(null);
      navigate(pathname + search, { replace: true });
    }
    const token = getAuthToken();
    if (token) {
      fetchUserDetails();
    } else {
      // navigate(generateLinkText('/unauthorized'));
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateWhatsappStatus = async (sessionId) => {
    await fetch({
      method: 'POST',
      url: `/dashboard/whatsapp-status/${sessionId}/`,
      data: { status: 'interacted' },
    });
    setSearchParams({}, { replace: true });
  };

  const fetchUserDetails = async () => {
    setIsLoading(true);
    try {
      const slug = getCompanySlugFromURL();
      const response = await fetch({
        method: 'GET',
        url: `/users/profile/?company=${slug}`,
      });
      setCurrentUser(response);
      if (searchParams.get('interacted')) {
        updateWhatsappStatus(searchParams.get('interacted'));
      }
      // navigate(generateLinkText('/'));
      if (
        location.pathname.includes('/not-found') ||
        location.pathname.includes('/unauthorized')
      ) {
        navigate(generateLinkText('/'));
      }
    } catch {
      navigate(generateLinkText('/unauthorized'));
    } finally {
      setIsLoading(false);
      setLoginLoading(false);
    }
  };

  if (isLoading) return <FullPageLoader />;

  if (
    currentUser?.company?.show_language_dropdown &&
    (currentUser?.company?.languages || []).length &&
    !currentUser?.language
  ) {
    return (
      <>
        <ConfigProvider
          theme={{
            ...theme,
            token: {
              ...theme.token,
              colorPrimary:
                currentUser?.company?.primary_color ||
                colorPalette.primary[500],
            },
          }}>
          <ThemeProvider
            theme={{
              colorPrimary:
                currentUser?.company?.primary_color ||
                colorPalette.primary[500],
            }}>
            <GlobalStyle />
            <ErrorBoundary>
              <SelectLanguage />
            </ErrorBoundary>
          </ThemeProvider>
        </ConfigProvider>
      </>
    );
  }

  return (
    <ConfigProvider
      theme={{
        ...theme,
        token: {
          ...theme.token,
          colorPrimary:
            currentUser?.company?.primary_color || colorPalette.primary[500],
        },
      }}>
      <ThemeProvider
        theme={{
          colorPrimary:
            currentUser?.company?.primary_color || colorPalette.primary[500],
        }}>
        <GlobalStyle />
        <ErrorBoundary>
          {APP_ENV === DEFAULT_ENVIRONMENT.DEMO ? (
            <DemoRoutes />
          ) : (
            <DefaultRoutes />
          )}
        </ErrorBoundary>
      </ThemeProvider>
    </ConfigProvider>
  );
}

export default App;
